import { useState } from 'react';
import {
  Modal,
  Stack,
  Button,
  Group,
  Select,
  Text,
  TextInput,
  SegmentedControl,
  Divider,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconList } from '@tabler/icons-react';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuditEvents, useCreateAuditEvent } from '@/hooks/api/useAudits';

interface AddToAuditModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (auditId: string) => void;
  selectedCount: number;
  isLoading?: boolean;
}

export function AddToAuditModal({
  opened,
  onClose,
  onSubmit,
  selectedCount,
  isLoading,
}: AddToAuditModalProps) {
  const { organizationId } = useOrganization();
  const { data: auditEvents = [] } = useAuditEvents(organizationId);
  const createAudit = useCreateAuditEvent();

  const [mode, setMode] = useState<'existing' | 'new'>('existing');

  const existingForm = useForm({
    initialValues: {
      auditId: '',
    },
    validate: {
      auditId: (value) => (value ? null : 'Please select an audit'),
    },
  });

  const newAuditForm = useForm({
    initialValues: {
      name: '',
      dueByDate: null as Date | null,
    },
    validate: {
      name: (value) => (value.trim() ? null : 'Audit name is required'),
    },
  });

  // Filter to only show active audits (draft or in_progress)
  const activeAudits = auditEvents.filter(
    (audit) => audit.status === 'draft' || audit.status === 'in_progress'
  );

  const auditOptions = activeAudits.map((audit) => ({
    value: audit.id,
    label: `${audit.name} (${audit.status})`,
  }));

  // Auto-switch to "new" mode if no active audits
  const effectiveMode = activeAudits.length === 0 ? 'new' : mode;

  const handleSubmitExisting = (values: typeof existingForm.values) => {
    onSubmit(values.auditId);
    existingForm.reset();
  };

  const handleSubmitNew = async (values: typeof newAuditForm.values) => {
    if (!organizationId) return;

    try {
      // Create the new audit
      const newAudit = await createAudit.mutateAsync({
        organization_id: organizationId,
        name: values.name.trim(),
        audit_date: new Date().toISOString(),
        due_by_date: values.dueByDate ? values.dueByDate.toISOString() : null,
        auditor_id: null,
        status: 'in_progress',
      });

      notifications.show({
        title: 'Audit Created',
        message: `"${values.name}" has been created`,
        color: 'green',
      });

      // Now add the assets to this new audit
      onSubmit(newAudit.id);
      newAuditForm.reset();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create audit',
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    existingForm.reset();
    newAuditForm.reset();
    setMode('existing');
    onClose();
  };

  const isSubmitting = isLoading || createAudit.isPending;

  return (
    <Modal opened={opened} onClose={handleClose} title="Add Assets to Audit" size="md">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Add {selectedCount} selected asset{selectedCount !== 1 ? 's' : ''} to an audit
        </Text>

        {activeAudits.length > 0 && (
          <SegmentedControl
            value={effectiveMode}
            onChange={(value) => setMode(value as 'existing' | 'new')}
            data={[
              {
                value: 'existing',
                label: (
                  <Group gap={6}>
                    <IconList size={16} />
                    <span>Select Existing</span>
                  </Group>
                ),
              },
              {
                value: 'new',
                label: (
                  <Group gap={6}>
                    <IconPlus size={16} />
                    <span>Create New</span>
                  </Group>
                ),
              },
            ]}
            fullWidth
          />
        )}

        {effectiveMode === 'existing' ? (
          <form onSubmit={existingForm.onSubmit(handleSubmitExisting)}>
            <Stack gap="md">
              <Select
                label="Select Audit"
                placeholder="Choose an audit"
                data={auditOptions}
                required
                {...existingForm.getInputProps('auditId')}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                  Add to Audit
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <form onSubmit={newAuditForm.onSubmit(handleSubmitNew)}>
            <Stack gap="md">
              {activeAudits.length === 0 && (
                <Text size="sm" c="dimmed">
                  No active audits found. Create a new one below.
                </Text>
              )}

              <TextInput
                label="Audit Name"
                placeholder="e.g., Q1 2025 Asset Audit"
                required
                {...newAuditForm.getInputProps('name')}
              />

              <DateInput
                label="Due Date (optional)"
                placeholder="Select due date"
                clearable
                {...newAuditForm.getInputProps('dueByDate')}
              />

              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                  Create & Add Assets
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </Stack>
    </Modal>
  );
}
